import { type FC, type ReactNode, useContext } from 'react';
import { useLazyLoadQuery, graphql } from 'react-relay';
import classnames from 'classnames';

import { GetHelpZendeskModalHeader } from './GetHelpZendeskModalHeader.tsx';
import { GetHelpZendeskFooter } from './GetHelpZendeskFooter.tsx';
import { GetHelpZendeskModalIntro } from './GetHelpZendeskModalIntro.tsx';
import { GetHelpZendeskOrdersList } from './GetHelpZendeskOrdersList.tsx';
import { GetHelpZendeskArticle } from './GetHelpZendeskArticle.tsx';
import { GetHelpZendeskOrderInfo } from './GetHelpZendeskOrderInfo.tsx';
import { GetHelpZendeskContactSupport } from './GetHelpZendeskContactSupport.tsx';
import { GetHelpZendeskArticlesSearchResults } from './GetHelpZendeskArticlesSearchResults.tsx';
import { GetHelpZendeskArticlesNoSearchResults } from './GetHelpZendeskArticlesNoSearchResults.tsx';
import { GetHelpZendeskModalCategory } from './GetHelpZendeskModalCategory.tsx';
import { GetHelpZendeskCommonQuestions } from './GetHelpZendeskCommonQuestions.tsx';
import { GetHelpZendeskCmsTopic } from './GetHelpZendeskCmsTopic.tsx';
import { GetHelpZendeskContactSupportSearch } from './GetHelpZendeskContactSupportSearch.tsx';
import { GetHelpZendeskSellerItemsList } from './GetHelpZendeskSellerItemsList.tsx';
import { GetHelpZendeskSellerItem } from './GetHelpZendeskSellerItem.tsx';
import { GetHelpZendeskSellerOrder } from './GetHelpZendeskSellerOrder.tsx';
import { GetHelpZendeskError } from './GetHelpZendeskError.tsx';

import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { GetHelpZendeskContext } from './GetHelpZendeskContext.ts';
import { STEPS } from './GetHelpZendeskConstants.ts';

import { type GetHelpZendeskModalRendererQuery } from './__generated__/GetHelpZendeskModalRendererQuery.graphql.ts';

import dibsCss from 'dibs-css';
import styles from './GetHelpZendeskModalRenderer.scss';

const GetHelpZendeskModalRenderer: FC<{ isDesktop: boolean }> = ({ isDesktop }) => {
    const { state } = useContext(GetHelpZendeskContext);
    const {
        userId,
        sellerId,
        step,
        orderId,
        articleId,
        searchQuery,
        isSeller,
        isCheckout,
        categoryId,
        topicKey,
        contactSupportSearchQuery,
        itemId,
    } = state;

    const { viewer } = useLazyLoadQuery<GetHelpZendeskModalRendererQuery>(
        graphql`
            query GetHelpZendeskModalRendererQuery(
                $articleId: String!
                $hasArticleId: Boolean!
                $hasOrderId: Boolean!
                $hasSearchQuery: Boolean!
                $hasSellerId: Boolean!
                $hasUserId: Boolean!
                $isLoggedOutUser: Boolean!
                $isSeller: Boolean
                $searchQuery: String
                $sellerId: String!
                $transactionId: ID!
                $orderId: String
                $userId: String!
                $isCheckout: Boolean
                $categoryId: String
                $topicKey: String!
                $userCountryCode: String = ""
                $contactSupportSearchQuery: String
                $hasContactSupportSearchQuery: Boolean!
                $itemId: String!
                $hasItemId: Boolean!
            ) {
                viewer {
                    ...GetHelpZendeskModalIntro_viewer
                    ...GetHelpZendeskOrderInfo_viewer
                    ...GetHelpZendeskArticle_viewer
                    ...GetHelpZendeskModalHeader_viewer
                    ...GetHelpZendeskArticlesSearchResults_viewer
                    ...GetHelpZendeskContactSupport_viewer
                    ...GetHelpZendeskFooter_viewer
                    ...GetHelpZendeskModalCategory_viewer
                    ...GetHelpZendeskCommonQuestions_viewer
                    ...GetHelpZendeskCmsTopic_viewer
                    ...GetHelpZendeskContactSupportSearch_viewer
                    ...GetHelpZendeskArticlesNoSearchResults_viewer
                    ...GetHelpZendeskSellerItem_viewer
                    ...GetHelpZendeskSellerOrder_viewer
                }
            }
        `,
        {
            articleId,
            hasArticleId: !!articleId,
            hasOrderId: !!orderId,
            hasSearchQuery: !!searchQuery,
            hasSellerId: !!sellerId,
            hasUserId: !!userId,
            isLoggedOutUser: !userId && !sellerId,
            isSeller,
            searchQuery,
            sellerId: sellerId || '',
            transactionId: orderId || '',
            orderId: orderId || '',
            userId: userId || '',
            isCheckout,
            categoryId: categoryId || '',
            topicKey: topicKey || '',
            userCountryCode: getUserSessionCountryCode(),
            contactSupportSearchQuery: contactSupportSearchQuery || '',
            hasContactSupportSearchQuery: !!contactSupportSearchQuery,
            itemId: itemId || '',
            hasItemId: !!itemId,
        }
    );

    const getModalContent = (): ReactNode => {
        switch (step) {
            case STEPS.INTRO:
                return <GetHelpZendeskModalIntro viewer={viewer} />;
            case STEPS.ORDERS_LIST:
                return <GetHelpZendeskOrdersList />;
            case STEPS.ITEMS_LIST:
                return <GetHelpZendeskSellerItemsList />;
            case STEPS.ORDER_INFO:
                return <GetHelpZendeskOrderInfo viewer={viewer} />;
            case STEPS.SEARCH_RESULTS:
                return <GetHelpZendeskArticlesSearchResults viewer={viewer} />;
            case STEPS.NO_SEARCH_RESULTS:
                return <GetHelpZendeskArticlesNoSearchResults viewer={viewer} />;
            case STEPS.ARTICLE:
                return <GetHelpZendeskArticle viewer={viewer} />;
            case STEPS.CONTACT_SUPPORT:
                return <GetHelpZendeskContactSupport viewer={viewer} />;
            case STEPS.CATEGORY:
                return <GetHelpZendeskModalCategory viewer={viewer} />;
            case STEPS.COMMON_QUESTIONS:
                return <GetHelpZendeskCommonQuestions viewer={viewer} />;
            case STEPS.CMS_TOPIC:
                return <GetHelpZendeskCmsTopic viewer={viewer} />;
            case STEPS.CONTACT_SUPPORT_SEARCH:
                return <GetHelpZendeskContactSupportSearch viewer={viewer} />;
            case STEPS.SELLER_ITEM:
                return <GetHelpZendeskSellerItem viewer={viewer} />;
            case STEPS.SELLER_ORDER:
                return <GetHelpZendeskSellerOrder viewer={viewer} />;
            case STEPS.ERROR:
                return <GetHelpZendeskError />;
            default:
                return <GetHelpZendeskModalIntro viewer={viewer} />;
        }
    };

    return (
        <>
            <GetHelpZendeskModalHeader viewer={viewer} />
            <div
                className={classnames(styles.content, dibsCss.pxSmall, dibsCss.mbLarge, {
                    [dibsCss.mbSmall]:
                        step === STEPS.INTRO ||
                        step === STEPS.SELLER_ORDER ||
                        step === STEPS.SELLER_ITEM,
                })}
            >
                {getModalContent()}
            </div>
            <div
                className={classnames({
                    [dibsCss.mtAuto]: !isDesktop,
                })}
            >
                <GetHelpZendeskFooter viewer={viewer} />
            </div>
        </>
    );
};

export { GetHelpZendeskModalRenderer };
